<template>
  <footer
    ref="footerRef"
    class="core-footer"
    :style="footerStyle"
    :class="{
      'core-footer--visible': isNormalFooterVisible,
      'core-footer--with-project-summary': hasProjectSummaryContainer,
      'core-footer--standalone': !hasProjectSummaryContainer,
    }">
    <div class="core-wrapper">
      <div v-if="hasProjectSummaryContainer" class="core-footer-separator"></div>
      <div class="core-footer-row">
        <div class="core-logos">
          <a
            href="https://arcexperts.ch"
            title="arcexperts"
            class="font-bold text-base text-black"
            target="_blank"
            rel="noopener noreferrer">
            a r c&nbsp;&nbsp;experts
          </a>
        </div>
        <!-- Pages with the project bar (upper footer row) host the buttons there -->
        <div v-if="showProjectTransfer" class="flex items-center gap-2">
          <button
            v-tooltip="tooltip(t('general.transfer.export.title'))"
            type="button"
            class="core-footer-icon-button core-action-button core-action-button--supporting"
            data-testid="project-export-button"
            :aria-label="t('general.transfer.export.title')"
            @click="transferMode = 'export'">
            <IconFileExport class="h-5 w-5" stroke="1.8" />
          </button>
          <button
            v-tooltip="tooltip(t('general.transfer.import.title'))"
            type="button"
            class="core-footer-icon-button core-action-button core-action-button--supporting"
            data-testid="project-import-button"
            :aria-label="t('general.transfer.import.title')"
            @click="transferMode = 'import'">
            <IconFileImport class="h-5 w-5" stroke="1.8" />
          </button>
        </div>
      </div>
    </div>
  </footer>
  <ProjectTransferModal
    v-if="showProjectTransfer"
    :open="transferMode !== null"
    :mode="transferMode ?? 'export'"
    @close="transferMode = null" />
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { IconFileExport, IconFileImport } from '@tabler/icons-vue'
import { useFooterLayoutState } from '@/helpers/footer-layout-state'
import { useProjectStore } from '@/stores/project'
import ProjectTransferModal, { type ProjectTransferMode } from '@/components/partials/modals/ProjectTransferModal.vue'

const { t } = useI18n()
const projectStore = useProjectStore()
const transferMode = ref<ProjectTransferMode | null>(null)
const footerRef = ref<HTMLElement | null>(null)
const scrollContainerRef = ref<HTMLElement | null>(null)
const isAtBottom = ref(false)
const userHasScrolled = ref(false)
const route = useRoute()
const {
  hasProjectSummaryContainer,
  isNormalFooterVisible,
  scrollbarWidth,
  setNormalFooterVisible,
  setNormalFooterHeight,
  setScrollbarWidth,
} = useFooterLayoutState()
const footerStyle = computed(() => ({
  right: `${scrollbarWidth.value}px`,
}))
const showProjectTransfer = computed(
  () => route.meta?.widget === 'project_units' && !!projectStore.getProject && !hasProjectSummaryContainer.value
)
const tooltip = (text: string) => ({ text, theme: { placement: 'top' } })

function measureFooterHeight() {
  setNormalFooterHeight(footerRef.value?.offsetHeight ?? 0)
}

function measureScrollbarWidth() {
  const scrollElement = scrollContainerRef.value
  if (!scrollElement) {
    setScrollbarWidth(0)
    return
  }
  setScrollbarWidth(scrollElement.offsetWidth - scrollElement.clientWidth)
}

function syncBottomState() {
  const scrollElement = scrollContainerRef.value
  if (!scrollElement) {
    isAtBottom.value = false
    return
  }
  // Initial layout can report 0/0 metrics and incorrectly look like "bottom reached".
  if (scrollElement.clientHeight <= 0 || scrollElement.scrollHeight <= 0) {
    isAtBottom.value = false
    return
  }
  isAtBottom.value = scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 1
}

function syncVisibility() {
  // Default assumption: user is not at bottom until they actually scroll.
  if (!userHasScrolled.value) {
    setNormalFooterVisible(false)
    return
  }
  setNormalFooterVisible(isAtBottom.value)
}

function onScroll() {
  userHasScrolled.value = true
  syncBottomState()
  syncVisibility()
}

function onResize() {
  syncBottomState()
  measureScrollbarWidth()
  measureFooterHeight()
  syncVisibility()
}

onMounted(async () => {
  userHasScrolled.value = false
  setNormalFooterVisible(false)
  scrollContainerRef.value = document.getElementById('core-widget-scroll')
  await nextTick()
  syncBottomState()
  measureScrollbarWidth()
  measureFooterHeight()
  syncVisibility()

  scrollContainerRef.value?.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize)
})

watch(
  () => hasProjectSummaryContainer.value,
  async () => {
    await nextTick()
    measureFooterHeight()
    syncVisibility()
  }
)

watch(
  () => isNormalFooterVisible.value,
  () => {
    if (isNormalFooterVisible.value) {
      measureFooterHeight()
    }
  }
)

watch(
  () => route.fullPath,
  async () => {
    userHasScrolled.value = false
    setNormalFooterVisible(false)
    await nextTick()
    syncBottomState()
    measureScrollbarWidth()
    measureFooterHeight()
    syncVisibility()
  }
)

onBeforeUnmount(() => {
  scrollContainerRef.value?.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onResize)
  setNormalFooterVisible(false)
  setNormalFooterHeight(0)
  setScrollbarWidth(0)
})
</script>

<style lang="postcss" scoped>
.core-footer {
  @apply fixed left-0 right-0 bottom-0 bg-white;
  z-index: 30;
  transform: translateY(100%);
  pointer-events: none;
  transition: transform 0.2s ease;

  &.core-footer--visible {
    transform: translateY(0);
    pointer-events: auto;
  }

  &.core-footer--standalone {
    @apply border-t border-neutral-300;
    box-shadow:
      0 -2px 4px rgba(0, 0, 0, 0.05),
      0 -8px 16px -10px rgba(0, 0, 0, 0.09);
  }

  .core-footer-separator {
    @apply border-t border-neutral-300;
  }

  .core-footer-row {
    @apply flex items-center justify-between gap-4 mt-8 mb-8;
  }

  .core-logos {
    @apply flex items-center;
    a {
      @apply inline-flex items-center leading-none;
    }
  }

  .core-footer-icon-button {
    @apply h-10 w-10 shrink-0 p-0;
  }
}
</style>
